'use client';
/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role --
   The table is laid out with `display: grid` (CMS-1772 R2), and Chrome and
   WebKit drop the native table semantics whenever `display` is overridden. The
   role below looks redundant against the markup but is what keeps the table in
   the accessibility tree, so it must stay. */

import classNames from 'classnames';
import { useState } from 'react';
import { LoanRewardPill } from '../../../components/LoanRewardPill';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { formatLoanRewardAmount } from '../../../utils/helpers/personalLoanReward';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { CELL_PADDING } from '../constants';
import { TRANSLATIONS } from '../i18n';
import { TotalRewardCellData } from '../types';
import { HotRewardModule } from './HotRewardModule';

interface TotalRewardCellProps {
  data: TotalRewardCellData;
  pushDataLayer?: PushDataLayerFn;
  index: number;
  titleTableBuilder: string;
}

const CELL_TEXT =
  'font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell';

/**
 * Total Reward cell, Personal Loan mode (CMS-1922 R2): the combined value as an
 * "Up to" pill, the per-source breakdown, and the "Show All" modal trigger. The
 * prefix sits on the pill only — the pill is the sum of the subtotals, so its
 * upper-bound qualifier covers both.
 *
 * Nothing qualifying in either source → "-" alone: no pill, breakdown or link.
 */
export const TotalRewardCell = ({
  data,
  pushDataLayer,
  index,
  titleTableBuilder,
}: TotalRewardCellProps) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const [isModuleOpen, setIsModuleOpen] = useState(false);
  const { breakdown } = data;

  const money = (value: number) =>
    formatLoanRewardAmount(value, breakdown ?? {}, context.currencyDisplay);

  // Exclusive first, provider second; a source with nothing qualifying is
  // omitted rather than shown as 0 (R2).
  const sources = [
    { label: staticText.reward_source_exclusive, value: breakdown?.exclusive },
    { label: staticText.reward_source_provider, value: breakdown?.provider },
  ].filter(
    (source): source is { label: string; value: number } =>
      source.value !== undefined,
  );

  return (
    <td
      role="cell"
      className={classNames(
        'border-l border-t border-borders/default',
        'min-h-table/cell-min-height',
        CELL_PADDING,
        // Long labels (「金融機構優惠」) wrap and the row grows; the column width
        // is unaffected (R2, CMS-1772 R3).
        'break-words',
      )}
    >
      {breakdown ? (
        <div className="flex h-full flex-col justify-center gap-spacing/padding/y-sm">
          <LoanRewardPill value={money(breakdown.total)} />
          <div className="flex flex-col gap-spacing/gap/soft-8px">
            {sources.map((source) => (
              <div
                key={source.label}
                className="flex flex-col gap-spacing/gap/soft-6px"
              >
                <span
                  className={classNames(
                    CELL_TEXT,
                    'text-text/secondary-lighter',
                  )}
                >
                  {source.label}
                </span>
                <span className="font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell text-text/primary">
                  {money(source.value)}
                </span>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIsModuleOpen(true)}
            aria-label={
              data.modal.productName
                ? `${staticText.reward_show_all}: ${data.modal.productName}`
                : undefined
            }
            className="cursor-pointer text-left font-font-weight/table-cell-strong text-font-size/table-cell leading-line-height/table-cell text-buttons/primary underline"
          >
            {staticText.reward_show_all}
          </button>
        </div>
      ) : (
        <div className="flex h-full flex-col justify-center">
          <span className={classNames(CELL_TEXT, 'text-text/primary')}>-</span>
        </div>
      )}
      {breakdown && isModuleOpen && (
        <HotRewardModule
          data={data.modal}
          title={staticText.module_pl_title}
          isOpen={isModuleOpen}
          onClose={() => setIsModuleOpen(false)}
          pushDataLayer={pushDataLayer}
          index={index}
          titleTableBuilder={titleTableBuilder}
        />
      )}
    </td>
  );
};
